import {
  PaymentRequestButtonElement,
  useElements,
} from '@stripe/react-stripe-js'
import { PaymentRequest } from '@stripe/stripe-js'
import { setNestedObjectValues, useFormikContext } from 'formik'
import { useEffect, useMemo } from 'react'
import { Amount } from '~/components/amount'
import { Button } from '~/components/button'
import { CheckboxField } from '~/components/checkbox'
import { Box, Flex } from '~/components/layout'
import { Meta } from '~/components/typography'
import { colors } from '~/styles/colors'
import { DonationRecurrenceInterval } from '~/types/graphql'

import { Values } from '../../hooks/use-form'
import { PaymentMethod } from '../../types'
import { getDonationRecurrenceIntervalLabel } from '~/utils/order-utils'
import { useToasts } from '~/context/toasts'

type Props = {
  paymentMethod: PaymentMethod
  paymentRequest?: PaymentRequest
}

export const Summary = ({ paymentMethod, paymentRequest }: Props) => {
  const { isSubmitting, values, isValid, submitForm, validateForm, setTouched } =
    useFormikContext<Values>()
  const elements = useElements()

  const toast = useToasts()

  const activeFees = useMemo(() => {
    return values.coverProcessingFees
      ? values.orderCalculation.withCoveringFees
      : values.orderCalculation.withoutCoveringFees
  }, [values.coverProcessingFees, values.orderCalculation])

  const submitPayment = async (event?: any) => {
    event?.preventDefault()

    if (isValid) {
      submitForm().catch(() => { })
    } else {
      const errors = await validateForm()
      if (errors) {
        // setNestedObjectValues walks nested error objects. A flat Object.keys
        // pass would set `customFields: true` and hide the per-field messages.
        setTouched(setNestedObjectValues(errors, true))
      }
      toast.error('Please fill out all required fields first.')
    }
  }

  const renderPaymentButton = () => {
    if (paymentRequest && paymentMethod === PaymentMethod.Other) {
      return (
        <PaymentRequestButtonElement
          onClick={submitPayment}
          options={{
            paymentRequest,
            style: {
              paymentRequestButton: {
                type: 'donate',
                height: '48px',
              },
            },
          }}
        />
      )
    }

    return (
      <Button
        // disabled={!isValid}
        onClick={submitPayment}
        width="100%"
        loading={isSubmitting}
      >
        Donate Now
      </Button>
    )
  }

  useEffect(() => {
    if (elements) {
      elements.update({
        amount: activeFees.gross,
        setup_future_usage:
          values.paymentFrequency == DonationRecurrenceInterval.None &&
            !values.switchedToMonthly
            ? null
            : 'off_session',
      })
    }
  }, [
    activeFees,
    elements,
    values.coverProcessingFees,
    values.paymentFrequency,
    values.switchedToMonthly,
  ])


  let paymentFrequencyString = ''

  if (values.switchedToMonthly) {
    paymentFrequencyString = 'Monthly'
  } else if (values.paymentFrequency !== DonationRecurrenceInterval.None) {
    paymentFrequencyString = getDonationRecurrenceIntervalLabel(values.paymentFrequency)
  }

  return (
    <Box
      padding={['30px 20px 40px', '30px 20px 40px', 40]}
      style={{ background: '#F7F7F7' }}
    >
      <Box marginBottom="0.5rem">
        <Flex justifyContent="space-between">
          <Meta regular>{paymentFrequencyString && `${paymentFrequencyString} `}Donation</Meta>
          <Meta regular>
            <Amount>{values.orderCalculation.withoutCoveringFees.gross}</Amount>
          </Meta>
        </Flex>
        {values.coverProcessingFees && (
          <Flex justifyContent="space-between" marginTop={6}>
            <Meta regular>Processing fees</Meta>
            <Meta regular>
              <Amount>{values.orderCalculation.withCoveringFees.fee}</Amount>
            </Meta>
          </Flex>
        )}
      </Box>

      <Box height={1} background={colors.whiteFog} marginY={16} />
      {values.paymentFrequency !== DonationRecurrenceInterval.Monthly && (
        <Box marginBottom="1rem">
          <CheckboxField
            label="Make my gift go further. Give monthly."
            name="switchedToMonthly"
          />
        </Box>
      )}

      <Flex justifyContent="space-between" marginBottom={16}>
        <Meta>Total Charge</Meta>
        <Meta>
          <Amount>{activeFees.gross}</Amount>
        </Meta>
      </Flex>
      {renderPaymentButton()}
    </Box>
  )
}
